// ABOUTME: Indicator component showing when the app is offline.
// ABOUTME: Displays a floating badge when network is unavailable with cached data info.

import { useState, useEffect, type FC } from "react"
import { useTranslation } from "react-i18next"
import { WifiOff, X } from "lucide-react"
import { useOnlineStatus } from "@shared/hooks/useOnlineStatus"

// Only a SUSTAINED offline state is worth a banner: WebView/mobile networks
// flap offline→online within a second or two on screen wake and Wi-Fi
// power-save, and flashing the badge on every flap reads as "the app is
// broken". Real outages comfortably outlast this window.
const OFFLINE_DEBOUNCE_MS = 2500

export const OfflineIndicator: FC = () => {
  const { t } = useTranslation()
  const isOnline = useOnlineStatus()
  const [isDismissed, setIsDismissed] = useState(false)
  const [wasOffline, setWasOffline] = useState(false)
  const [showOffline, setShowOffline] = useState(false)
  const [prevOnline, setPrevOnline] = useState(isOnline)

  // Back online: disarm the offline banner in the render that sees it, so the
  // next outage is debounced from scratch.
  if (isOnline !== prevOnline) {
    setPrevOnline(isOnline)
    if (isOnline) setShowOffline(false)
  }

  // Debounced offline: arm the banner (and the "back online" follow-up) only
  // after the offline state has persisted. A flap that recovers within the
  // window never shows anything.
  useEffect(() => {
    if (isOnline) return
    const timer = setTimeout(() => {
      setIsDismissed(false)
      setWasOffline(true)
      setShowOffline(true)
    }, OFFLINE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [isOnline])

  // Show "back online" briefly when reconnecting
  useEffect(() => {
    if (isOnline && wasOffline) {
      const timer = setTimeout(() => {
        setWasOffline(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isOnline, wasOffline])

  if (isOnline && !wasOffline) {
    return null
  }

  // Offline, but not yet past the debounce window — stay quiet.
  if (!isOnline && !showOffline) {
    return null
  }

  if (isDismissed) {
    return null
  }

  const isBackOnline = isOnline && wasOffline

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 py-2 px-3 rounded-lg shadow-lg text-sm font-medium transition-all duration-300 ${
        isBackOnline ? "bg-green-600 text-white" : "bg-amber-500 text-white"
      }`}
    >
      <WifiOff className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">{isBackOnline ? t("common.backOnline") : t("common.offlineMode")}</span>
      <button
        onClick={() => setIsDismissed(true)}
        className="ml-1 p-0.5 rounded hover:bg-white/20 transition-colors flex-shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
